/**
 * React component for minimap.
 * Displays map overview with camera view indicator and click-to-navigate.
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { tileToIsometricWorld } from '@shared/utils/coordinates';
import { TerrainType } from '@shared/constants/terrain';

interface MinimapProps {
  scene: unknown | null;
}

interface MapBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  width: number;
  height: number;
}

export default function Minimap({ scene }: MinimapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cameraView, setCameraView] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!scene || typeof scene !== 'object' || scene === null) return;

    const sceneWithMethods = scene as {
      getCameraController?: () => {
        getCamera?: () => {
          scrollX: number;
          scrollY: number;
          width: number;
          height: number;
          zoom: number;
        };
        moveCameraTo?: (x: number, y: number) => void;
      } | null;
      getMapData?: () => {
        getDimensions: () => { width: number; height: number };
        getTileSize: () => number;
        getTerrain: (x: number, y: number) => TerrainType;
      } | null;
      cameras?: {
        main?: {
          scrollX: number;
          scrollY: number;
          width: number;
          height: number;
          zoom: number;
        };
      };
    };

    const mapData = sceneWithMethods.getMapData?.();
    if (!mapData) return;

    const dimensions = mapData.getDimensions();
    const tileSize = mapData.getTileSize();

    // Calculate isometric world bounds (same as CameraController)
    const topLeft = tileToIsometricWorld(0, 0, tileSize);
    const topRight = tileToIsometricWorld(dimensions.width, 0, tileSize);
    const bottomLeft = tileToIsometricWorld(0, dimensions.height, tileSize);
    const bottomRight = tileToIsometricWorld(dimensions.width, dimensions.height, tileSize);

    const minX = Math.min(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
    const maxX = Math.max(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
    const minY = Math.min(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);
    const maxY = Math.max(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);

    const bounds: MapBounds = {
      minX,
      maxX,
      minY,
      maxY,
      width: maxX - minX,
      height: maxY - minY,
    };

    setMapBounds(bounds);

    // Draw map preview on canvas
    const drawMap = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      // Set canvas size to match container size for better quality
      const container = canvas.parentElement;
      if (container) {
        const rect = container.getBoundingClientRect();
        canvas.width = rect.width * window.devicePixelRatio || 1;
        canvas.height = rect.height * window.devicePixelRatio || 1;
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;

      // Scale context for high DPI displays
      const dpr = window.devicePixelRatio || 1;
      ctx.scale(dpr, dpr);

      // Clear canvas
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(0, 0, canvasWidth / dpr, canvasHeight / dpr);

      // Draw terrain tiles
      const scaleX = canvasWidth / dpr / bounds.width;
      const scaleY = canvasHeight / dpr / bounds.height;

      // Terrain colors (matching TERRAIN_COLORS from TilemapScene)
      const terrainColors: Record<TerrainType, string> = {
        [TerrainType.GRASS]: '#4a7c59',
        [TerrainType.DIRT]: '#8b7355',
        [TerrainType.STONE]: '#888888',
        [TerrainType.WATER]: '#3a5f8f',
        [TerrainType.CLIFFS]: '#1a1a1a',
        [TerrainType.MINE_NODE]: '#ffd700',
      };

      for (let y = 0; y < dimensions.height; y++) {
        for (let x = 0; x < dimensions.width; x++) {
          const isoPos = tileToIsometricWorld(x, y, tileSize);
          const terrainType = mapData.getTerrain(x, y);
          const color = terrainColors[terrainType] || terrainColors[TerrainType.GRASS];

          // Convert isometric position to minimap coordinates
          const minimapX = (isoPos.x - bounds.minX) * scaleX;
          const minimapY = (isoPos.y - bounds.minY) * scaleY;

          ctx.fillStyle = color;
          // Draw larger dots to fill the minimap better
          const dotSize = Math.max(1.5, Math.min(3, Math.max(scaleX, scaleY) * 0.8));
          ctx.fillRect(minimapX - dotSize / 2, minimapY - dotSize / 2, dotSize, dotSize);
        }
      }
    };

    // Draw map initially and on resize
    drawMap();
    const resizeObserver = new ResizeObserver(() => {
      drawMap();
    });
    const container = canvasRef.current?.parentElement;
    if (container) {
      resizeObserver.observe(container);
    }

    // Update camera view indicator periodically
    const updateCameraView = () => {
      const cameraController = sceneWithMethods.getCameraController?.();

      if (cameraController && mapData) {
        const camera = cameraController.getCamera?.();
        if (camera) {
          setCameraView({
            x: camera.scrollX,
            y: camera.scrollY,
            width: camera.width / camera.zoom,
            height: camera.height / camera.zoom,
          });
        }
      } else if (sceneWithMethods.cameras?.main) {
        const camera = sceneWithMethods.cameras.main;
        setCameraView({
          x: camera.scrollX,
          y: camera.scrollY,
          width: camera.width / camera.zoom,
          height: camera.height / camera.zoom,
        });
      }
    };

    const interval = setInterval(updateCameraView, 100); // Update every 100ms
    updateCameraView(); // Initial update

    return () => {
      clearInterval(interval);
      resizeObserver.disconnect();
    };
  }, [scene]);

  const moveCameraToPosition = useCallback(
    (clientX: number, clientY: number) => {
      if (!scene || !canvasRef.current || !mapBounds) return;

      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;

      // Clamp coordinates to canvas bounds (use display size, not canvas pixel size)
      const displayWidth = rect.width;
      const displayHeight = rect.height;
      const clampedX = Math.max(0, Math.min(displayWidth, x));
      const clampedY = Math.max(0, Math.min(displayHeight, y));

      const sceneWithMethods = scene as {
        getCameraController?: () => {
          getCamera?: () => {
            width: number;
            height: number;
            zoom: number;
          };
          moveCameraTo?: (x: number, y: number) => void;
        } | null;
        cameras?: {
          main?: {
            width: number;
            height: number;
            zoom: number;
            setScroll: (x: number, y: number) => void;
          };
        };
      };

      // Convert minimap coordinates to isometric world coordinates
      const scaleX = mapBounds.width / displayWidth;
      const scaleY = mapBounds.height / displayHeight;

      const targetWorldX = mapBounds.minX + clampedX * scaleX;
      const targetWorldY = mapBounds.minY + clampedY * scaleY;

      // Get camera viewport size to center the camera on the target position
      let viewportWidth = 0;
      let viewportHeight = 0;

      const cameraController = sceneWithMethods.getCameraController?.();
      if (cameraController) {
        const camera = cameraController.getCamera?.();
        if (camera) {
          viewportWidth = camera.width / camera.zoom;
          viewportHeight = camera.height / camera.zoom;
        }
      } else if (sceneWithMethods.cameras?.main) {
        const camera = sceneWithMethods.cameras.main;
        viewportWidth = camera.width / camera.zoom;
        viewportHeight = camera.height / camera.zoom;
      }

      // Center camera on target position (subtract half viewport size)
      const cameraX = targetWorldX - viewportWidth / 2;
      const cameraY = targetWorldY - viewportHeight / 2;

      // Move camera to centered position
      if (cameraController?.moveCameraTo) {
        cameraController.moveCameraTo(cameraX, cameraY);
      } else if (sceneWithMethods.cameras?.main?.setScroll) {
        sceneWithMethods.cameras.main.setScroll(cameraX, cameraY);
      }
    },
    [scene, mapBounds]
  );

  const handleMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (event.button !== 0) return; // Only handle left mouse button
    setIsDragging(true);
    moveCameraToPosition(event.clientX, event.clientY);
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (isDragging) {
      moveCameraToPosition(event.clientX, event.clientY);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Handle global mouse events for dragging outside canvas
  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalMouseMove = (event: MouseEvent) => {
      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        // Only update if mouse is still over the canvas area
        if (
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom
        ) {
          moveCameraToPosition(event.clientX, event.clientY);
        }
      }
    };

    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, moveCameraToPosition]);

  return (
    <div className="w-full h-48 bg-gray-700 rounded relative overflow-hidden" data-minimap>
      <canvas
        ref={canvasRef}
        className="w-full h-full cursor-pointer"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        title="Click or drag to navigate"
      />
      {cameraView && mapBounds && (
        <div
          className="absolute border-2 border-blue-500 bg-blue-500 bg-opacity-20 pointer-events-none"
          style={{
            left: `${((cameraView.x - mapBounds.minX) / mapBounds.width) * 100}%`,
            top: `${((cameraView.y - mapBounds.minY) / mapBounds.height) * 100}%`,
            width: `${(cameraView.width / mapBounds.width) * 100}%`,
            height: `${(cameraView.height / mapBounds.height) * 100}%`,
          }}
        />
      )}
    </div>
  );
}
